import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import taperFadeAsset from "@/assets/opt/taper-fade-tailors-barber.webp";
import { Faq } from "@/components/Faq";
import { Button } from "@/components/ui/button";
import { breadcrumbSchema, faqSchema, jsonLd, SITE_URL, absUrl } from "@/lib/seo";
import { withBookingUtm } from "@/lib/booking";
import { internalPath } from "@/lib/paths";
import { ServiceHero } from "@/components/ServiceHero";
import { FadeGallery } from "@/components/FadeGallery";
import { FadeVideos } from "@/components/FadeVideos";
import lowTaperBak from "@/assets/low-taper-fade-bakifran.webp.asset.json";
import lowTaperSida from "@/assets/low-taper-fade-sida-skagg.webp.asset.json";
import nackeVideo from "@/assets/taper-fade-nacke.mp4.asset.json";
import nackeVideoWebm from "@/assets/taper-fade-nacke.webm.asset.json";
import nackePoster from "@/assets/taper-fade-nacke-poster.jpg.asset.json";
import sidornaVideo from "@/assets/taper-fade-sidorna.mp4.asset.json";
import sidornaVideoWebm from "@/assets/taper-fade-sidorna.webm.asset.json";
import sidornaPoster from "@/assets/taper-fade-sidorna-poster.jpg.asset.json";

const BOOK_URL = withBookingUtm(
  "https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678",
);

const taperDefinition =
  "En taper fade är en gradvis övergång där håret blir kortare nedåt mot nacken och tinningarna, utan att huden blottas som vid en skin fade. De tre vanligaste varianterna är low, mid och high taper. Barberaren bygger övergången med maskin i flera längder för ett mjukt och jämnt resultat.";

const skinDefinition =
  "En skin fade är en gradvis övergång där huden syns längst ned vid nacken och tinningarna, till skillnad från en taper fade som lämnar kort hår kvar. Barberaren bygger övergången uppåt i flera längder och använder rakkniv eller maskin utan skydd längst ned för ett rent och tydligt avslut.";

const fadeDifference =
  "En taper fade lämnar kort hår kvar längst ned vid nacken och tinningarna. En skin fade går hela vägen ned till synlig hud. Taper fade ger därför ett mjukare resultat, medan skin fade skapar tydligare kontrast.";

const fadePrice =
  "En fade kostar 450 kr hos Tailor's Barber. Priset gäller taper fade och skin fade på alla våra fyra salonger i Stockholm.";

const fadeTypes = [
  {
    name: "Low Taper Fade",
    desc: "Låg övergång precis ovanför öronen och nacken. Vår mest bokade fade.",
  },
  { name: "Low Fade", desc: "Diskret gradering längs nacklinjen, klassisk och lätt att bära." },
  {
    name: "Mid Fade",
    desc: "Övergången startar mitt på sidan av huvudet. Balanserar klassiskt och modernt.",
  },
  {
    name: "High Taper Fade",
    desc: "Övergången börjar högt vid tinningen. Ger ett djärvt, modernt utseende.",
  },
  { name: "Skin Fade / Bald Fade", desc: "Graderas ända ned till ren hud. Maximal kontrast." },
  {
    name: "Taper Fade",
    desc: "Klassisk gradvis övergång längs sidan och nacken, utan att gå till noll.",
  },
  {
    name: "Drop Fade",
    desc: "Övergången följer örats form nedåt mot nacken för ett unikt utseende.",
  },
  {
    name: "Textured Fringe + Low Taper",
    desc: "Texturerad lugg med låg fade, den mest populära fade-frisyren just nu.",
  },
];

const steps = [
  "Konsultation: barberaren diskuterar önskad längd och stil med kunden",
  "Grundlängd: hårklippning av toppen till önskad längd med sax eller maskin",
  "Linjering: tydliga linjer sätts vid pannlinjen, sidburna och nacken",
  "Gradientteknik: maskin med olika kamriktningar skapar den mjuka övergången",
  "Blending: sax-over-comb och clipper-over-comb för att smälta samman längderna",
  "Skinlinjen: om skin fade, nollgraderas det sista med nollmaskin eller rakkniv",
  "Detaljarbete och styling: slutlig formning och styling med produkt efter önskemål",
];

const locations = [
  {
    name: "Täby Centrum",
    address: "Biblioteksgången 1, 183 34 Täby",
    hours: "Måndag till fredag 10 till 20 | Lördag till söndag 10 till 18",
    book: "https://www.bokadirekt.se/places/tailors-barber-taby-centrum-37677",
  },
  {
    name: "Åhléns City (Centrala Stockholm)",
    address: "Klarabergsgatan 50, våning 3, 1 minut från T-Centralen",
    hours: "Måndag till fredag 10 till 20 | Lördag 10 till 19 | Söndag 11 till 19",
    book: "https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678",
  },
  {
    name: "Åkersberga, ICA Maxi",
    address: "Långhundravägen 9, 184 44 Åkersberga",
    hours: "Måndag till fredag 10 till 19 | Lördag till söndag 10 till 18",
    book: "https://www.bokadirekt.se/places/tailors-barber-akersberga-ica-maxi-huset--37682",
  },
  {
    name: "Arninge, ICA Maxi",
    address: "Kundvägen 1D, 187 66 Täby",
    hours: "Måndag till fredag 10 till 19 | Lördag till söndag 10 till 18",
    book: "https://www.bokadirekt.se/places/tailors-barber-arninge-ica-maxi-huset--61598",
  },
];

const faq = [
  { q: "Vad är en taper fade?", a: taperDefinition },
  { q: "Vad är en skin fade?", a: skinDefinition },
  { q: "Vad är skillnaden mellan taper fade och skin fade?", a: fadeDifference },
  { q: "Vad kostar en fade i Stockholm?", a: fadePrice },
  {
    q: "Vad är en low taper fade?",
    a: "En low taper fade är en fade där övergången börjar lågt, precis ovanför öronen och längs nacklinjen, medan längden på sidorna och toppen behålls. Det ger ett diskret resultat som växer ut snyggt.",
  },
  {
    q: "Vad är skillnaden mellan low taper och high taper fade?",
    a: "Skillnaden är var övergången startar. Low taper börjar strax ovanför öronen och ger en mjuk, diskret linje. High taper börjar högt vid tinningen och ger mer kontrast och en tydligare modern siluett.",
  },
  {
    q: "Vilken fade frisyr är populärast just nu?",
    a: "Low taper fade i kombination med en texturerad lugg (textured fringe) är den mest efterfrågade fade-frisyren hos oss just nu, följt av skin fade.",
  },

  {
    q: "Hur länge håller en taper fade?",
    a: "En taper fade håller vanligtvis 3 till 5 veckor beroende på hur snabbt ditt hår växer och vilken fade-nivå du valt. På en skin fade syns utväxningen snabbare än på en klassisk taper fade.",
  },
  {
    q: "Kan jag komma på drop-in för taper fade?",
    a: "Ja, drop-in är välkommet i mån av tid på alla våra salonger. Vi rekommenderar onlinebokning via BokaDirekt för att garantera tid, speciellt på helger.",
  },
  {
    q: "Hur lång tid tar en taper fade?",
    a: "En taper fade tar normalt 30 till 45 minuter. Med skäggtrimning i kombination kan besöket ta upp till 60 minuter.",
  },
  {
    q: "Vilken fade passar mig?",
    a: "Det beror på din hårtyp, ansiktsform och önskad stil. Vår barberare hjälper alltid till med en konsultation inför klippningen för att hitta rätt look.",
  },
  {
    q: "Kan man kombinera taper fade med skägg?",
    a: "Absolut. En taper fade kombineras ofta med ett välformat skägg för ett polerat, komplett look. Vi erbjuder Herrklippning + Skäggtrimning för 680 kr.",
  },
  {
    q: "Gör ni skin fade eller nollfade?",
    a: "Ja, vi utför alla varianter av fade, bland annat taper fade, skin fade, bald fade, drop fade, low fade, mid fade och high fade.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Taper Fade & Low Taper Fade",
  alternateName: [
    "Low Taper Fade",
    "High Taper Fade",
    "Low Fade",
    "Skin Fade",
    "Fade Frisyr",
    "Bald Fade",
  ],
  description:
    "Low taper fade, high taper fade, skin fade och klassisk taper fade klippt av erfarna barberare hos Tailor's Barber i Stockholm.",
  image: [absUrl(lowTaperBak.url)],

  provider: {
    "@type": "BarberShop",
    name: "Tailor's Barber",
    url: SITE_URL,
  },
  areaServed: { "@type": "City", name: "Stockholm" },
  offers: { "@type": "Offer", price: "450", priceCurrency: "SEK" },
};

export const Route = createFileRoute("/taper-fade")({
  head: () => ({
    meta: [
      { title: "Taper fade: vad det är och vad det kostar | Tailor's Barber" },
      {
        name: "description",
        content:
          "En taper fade är en mjuk övergång där håret kortas mot nacke och tinningar utan att huden syns. Low, mid och high. 450 kr på våra fyra salonger i Stockholm.",
      },
      {
        property: "og:title",
        content: "Taper fade: vad det är och vad det kostar | Tailor's Barber",
      },
      {
        property: "og:description",
        content:
          "En taper fade är en mjuk övergång där håret kortas mot nacke och tinningar utan att huden syns. Low, mid och high. 450 kr på våra fyra salonger i Stockholm.",
      },

      { property: "og:url", content: absUrl("/taper-fade") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absUrl("/taper-fade") }],
    scripts: [
      jsonLd(serviceSchema),
      jsonLd(faqSchema(faq)),
      jsonLd(
        breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Taper Fade", url: "/taper-fade" },
        ]),
      ),
      jsonLd({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: "Taper fade i nacken hos Tailor's Barber Stockholm",
        description:
          "Barberare tonar ned nacklinjen med maskin i en taper fade hos Tailor's Barber i Stockholm.",
        thumbnailUrl: [absUrl(nackePoster.url)],
        uploadDate: "2026-08-21",
        duration: "PT5S",
        contentUrl: absUrl(nackeVideo.url),
      }),
      jsonLd({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: "Taper fade på sidorna hos Tailor's Barber Stockholm",
        description: "Sidorna jämnas ut med sax för att blenda taper faden mot längden på toppen.",
        thumbnailUrl: [absUrl(sidornaPoster.url)],
        uploadDate: "2026-08-21",
        duration: "PT5S",
        contentUrl: absUrl(sidornaVideo.url),
      }),
    ],
  }),
  component: TaperFadePage,
});

function TaperFadePage() {
  return (
    <SiteShell>
      <ServiceHero
        breadcrumbName="Taper Fade"
        eyebrow="Taper Fade & Low Taper Fade"
        title={<>Taper Fade och Low Taper Fade hos Tailor&apos;s Barber</>}
        directAnswerHeading="Vad är en taper fade?"
        directAnswer={taperDefinition}
        intro={
          <>
            Låg, mellan eller hög, vi klipper alla varianter av fade frisyr: low taper fade, high
            taper fade, skin fade och klassisk taper. Våra barberare bygger övergången för hand så
            att den sitter perfekt mot din hårtyp och växer ut snyggt. Fyra salonger i Stockholm med
            drop-in och onlinebokning. Söker du en{" "}
            <Link
              to={internalPath("/barberare-stockholm-ahlens-city/")}
              className="underline font-medium"
            >
              Barber Stockholm
            </Link>{" "}
            i city eller en{" "}
            <Link
              to={internalPath("/barberare-stockholm-ahlens-city/")}
              className="underline font-medium"
            >
              Barberare Stockholm City
            </Link>{" "}
            nära T-Centralen? Boka taper fade på Åhléns City.
          </>
        }
        priceLabel="Från 450 kr"
        durationLabel="40 min"
        ctaLabel="Boka taper fade"
        ctaHref={BOOK_URL}
        image={taperFadeAsset}
        imageAlt="Low taper fade från sidan med kort övergång vid tinningen hos Tailor's Barber"
        eager
      />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Taper fade för moderna och klassiska frisyrer</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Taper fade är en av de mest populära herrfrisyrerna i Sverige just nu, och passar både
          moderna och klassiska frisyrer. Den kan kombineras med pompadour, crew cut, buzz cut eller
          textured crop. Se fler{" "}
          <Link to={internalPath("/herrfrisyrer-2026/")} className="underline font-medium">
            herrfrisyrer 2026
          </Link>{" "}
          om du vill ha inspiration inför besöket.
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Att fadea för hand kräver träning, och det är just det som skiljer en{" "}
          <Link to={internalPath("/barbershop-stockholm/")} className="underline font-medium">
            barbershop från en frisörsalong
          </Link>{" "}
          . Hos oss är herrklippning, fade och skägg det vi gör hela dagarna.
        </p>
      </section>

      <FadeGallery
        heading="Taper fade klippta hos oss"
        intro="Riktiga resultat från våra barberarstolar, låg taper bakifrån och från sidan mot skägget."
        items={[
          {
            src: lowTaperBak.url,
            alt: "Low taper fade bakifrån med lockigt hår på toppen",
            caption:
              "Low taper fade bakifrån, med mjuk övergång längs nacklinjen och längden kvar på toppen.",
          },
          {
            src: lowTaperSida.url,
            alt: "Low taper fade från sidan där faden kopplas ihop med skägget",
            caption:
              "Low taper fade från sidan, där övergången kopplas ihop med skägget vid kindlinjen.",
          },
        ]}
      />

      <FadeVideos
        heading="Se faden växa fram"
        intro="Två korta klipp från stolen: nacklinjen med maskin och sidorna med sax."
        items={[
          {
            src: nackeVideo.url,
            webm: nackeVideoWebm.url,
            poster: nackePoster.url,
            title: "Taper fade i nacken",
            caption: "Nacken tonas ned med maskin, här bestäms hur lågt faden startar.",
          },
          {
            src: sidornaVideo.url,
            webm: sidornaVideoWebm.url,
            poster: sidornaPoster.url,
            title: "Taper fade på sidorna",
            caption: "Sidorna jämnas ut med sax för att blenda mot längden på toppen.",
          },
        ]}
      />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Skillnaden mellan taper fade och skin fade</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          En taper fade lämnar kort hår kvar längst ned vid nacken och tinningarna. En skin fade går
          hela vägen ned till synlig hud. Taper fade ger därför ett mjukare resultat, medan skin
          fade skapar tydligare kontrast. Läs mer om hur en{" "}
          <Link to="/skin-fade/" className="underline font-medium">
            skin fade
          </Link>{" "}
          byggs och vilka varianter som finns.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">
          Low taper fade, den mest efterfrågade fade-frisyren
        </h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          En low taper fade är den variant våra barberare klipper allra oftast. Övergången startar
          lågt, precis ovanför öronen och längs nacklinjen, medan längden på sidorna och toppen
          behålls. Det gör att en low taper passar både på jobbet och på helgen, och den växer ut
          snyggt utan skarpa kanter.
        </p>
        <div className="mt-6 grid gap-5 md:grid-cols-3 max-w-4xl">
          <div className="card-elev p-6">
            <h3 className="text-lg font-medium">Low taper fade</h3>
            <p className="mt-2 text-sm text-foreground/80">
              Låg, diskret övergång. Snyggast med lite längd kvar på toppen, exempelvis crop,
              sidbena eller bakåtkammat.
            </p>
          </div>
          <div className="card-elev p-6">
            <h3 className="text-lg font-medium">Textured fringe + low taper</h3>
            <p className="mt-2 text-sm text-foreground/80">
              Texturerad lugg fram och low taper på sidorna, den mest populära fade-frisyren bland
              killar just nu.
            </p>
          </div>
          <div className="card-elev p-6">
            <h3 className="text-lg font-medium">High taper fade</h3>
            <p className="mt-2 text-sm text-foreground/80">
              Övergången börjar högt vid tinningen. Ger maximal kontrast och en tydligt modern
              siluett.
            </p>
          </div>
        </div>
        <p className="mt-6 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Osäker på vilken nivå du ska välja? Berätta bara hur kort du vill ha det vid öronen, så
          guidar barberaren dig mellan low, mid och high taper under konsultationen.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Typer av fade vi erbjuder</h2>
        <ul className="mt-6 grid gap-3 md:grid-cols-2 max-w-4xl">
          {fadeTypes.map((t) => (
            <li key={t.name} className="card-elev p-5">
              <strong>{t.name}</strong>
              <p className="mt-1 text-sm text-foreground/80">{t.desc}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Pris för taper fade i Stockholm</h2>
        <ul className="mt-4 space-y-2 text-base md:text-lg text-foreground/85 max-w-3xl">
          <li>
            <strong>Taper fade (Herrklippning):</strong> 450 kr
          </li>
          <li>
            <strong>Skin fade:</strong> 450 kr
          </li>
          <li>
            <strong>Herrklippning + Skäggtrimning:</strong> 680 kr
          </li>
        </ul>
        <p className="mt-4 text-foreground/70 max-w-3xl">
          Priset inkluderar konsultation, klippning och styling. Inga dolda avgifter. Se hela vår{" "}
          <Link to="/priser/" className="underline">
            prislista
          </Link>
          .
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Hur gör man en taper fade? Steg för steg</h2>
        <ol className="mt-6 space-y-3 max-w-3xl list-decimal list-inside text-base md:text-lg text-foreground/85">
          {steps.map((s, i) => (
            <li key={i} className="leading-relaxed">
              {s}
            </li>
          ))}
        </ol>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">
          Boka taper fade på en av våra fyra salonger i Stockholm
        </h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {locations.map((l) => (
            <article key={l.name} className="card-elev p-6">
              <h3 className="text-xl font-medium">{l.name}</h3>
              <p className="mt-2 text-sm text-foreground/80">{l.address}</p>
              <p className="mt-1 text-sm text-foreground/70">{l.hours}</p>
              <div className="mt-4">
                <Button asChild size="sm">
                  <a href={l.book} target="_blank" rel="noopener noreferrer">
                    Boka här
                  </a>
                </Button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Relaterade behandlingar</h2>
        <ul className="mt-4 flex flex-wrap gap-3">
          <li>
            <Link to="/herrklippning/" className="btn-outline">
              Herrklippning
            </Link>
          </li>
          <li>
            <Link to="/skaggtrimning/" className="btn-outline">
              Skäggtrimning
            </Link>
          </li>
          <li>
            <Link to="/herrklippning-skaggtrimning/" className="btn-outline">
              Klippning & Skägg
            </Link>
          </li>
          <li>
            <Link to="/" className="btn-outline">
              Barberare Stockholm
            </Link>
          </li>
        </ul>
      </section>

      <Faq items={faq} heading="Vanliga frågor om taper fade" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Redo för din taper fade?</h2>
          <p className="mt-3 text-foreground/80">Boka online eller kom förbi på drop-in.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">
                Boka taper fade
              </a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
